import { Head, Link, router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { ArrowLeft, Plus, Trash2, HelpCircle } from 'lucide-react';
import { useState, useRef } from 'react';

interface SplitJob {
  id: number;
  start_page?: number;
  end_page?: number;
  header_search_string?: string;
  split_rules?: string;
  status: string;
  start_pages?: any;
  processing_job_id?: string;
}

interface Document {
  id: number;
  name: string;
  page_count?: number;
  split_jobs: SplitJob[];
}

interface SplitJobEditProps {
  document: Document;
  splitJob: SplitJob;
}

const DIVISOR_TYPES = [
  'title', 'volume', 'part', 'division', 'subdivision', 'article', 
  'chapter', 'section', 'subsection', 'clause', 'subclause', 
  'paragraph', 'subchapter', 'provision', 'subpart', 'URL'
];

interface DivisorSelection {
  id: string;
  type: string;
}

export default function SplitJobEdit({ document, splitJob }: SplitJobEditProps) {
  const [jsonData, setJsonData] = useState(() => {
    if (splitJob.start_pages) {
      return JSON.stringify(splitJob.start_pages, null, 2);
    }
    return '';
  });
  
  const [overlapPages, setOverlapPages] = useState(false);
  const [extractPrompt, setExtractPrompt] = useState('');
  const [jsonMap, setJsonMap] = useState('');
  const [chunkingStrategy, setChunkingStrategy] = useState('size_aware');
  const [divisorSelections, setDivisorSelections] = useState<DivisorSelection[]>([
    { id: Math.random().toString(), type: '' }
  ]);
  const [saveStatus, setSaveStatus] = useState<{message: string, type: 'success' | 'error' | 'loading'} | null>(null);
  const [showReadme, setShowReadme] = useState(false);
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const cleanJsonData = async () => {
    try {
      let parsed: any;
      
      // Try to parse the JSON
      try {
        parsed = JSON.parse(jsonData);
      } catch (initialError) {
        // Check if it's an escaped JSON string
        if (jsonData.startsWith('"') && jsonData.endsWith('"')) {
          try {
            const unescapedString = JSON.parse(jsonData);
            parsed = JSON.parse(unescapedString);
          } catch (secondError) {
            throw initialError;
          }
        } else {
          throw initialError;
        }
      }

      // Check if we're dealing with character-by-character JSON
      const keys = Object.keys(parsed);
      const isCharByChar = keys.length > 100 && 
                           keys.every(key => !isNaN(parseInt(key))) &&
                           keys.includes("0") && 
                           keys.includes("1");

      if (isCharByChar) {
        // Join all characters to reconstruct the original JSON
        const reconstructedJson = Object.values(parsed).join('');
        const innerParsed = JSON.parse(reconstructedJson);
        
        // Remove "no" entries
        const cleaned: Record<string, any> = {};
        Object.entries(innerParsed).forEach(([key, value]) => {
          if (value !== "no") {
            cleaned[key] = value;
          }
        });
        
        setJsonData(JSON.stringify(cleaned, null, 2));
        await saveJsonToDatabase(cleaned);
      } else {
        // Standard JSON processing
        if (typeof parsed === 'string' && parsed.includes('\":\"')) {
          try {
            parsed = JSON.parse(parsed);
          } catch (e) {
            // Use as is
          }
        }
        
        // Remove "no" entries
        const cleaned: Record<string, any> = {};
        Object.entries(parsed).forEach(([key, value]) => {
          if (value !== "no") {
            cleaned[key] = value;
          }
        });
        
        setJsonData(JSON.stringify(cleaned, null, 2));
        await saveJsonToDatabase(cleaned);
      }
    } catch (error) {
      console.error("JSON parsing error:", error);
      setSaveStatus({message: "Invalid JSON! Please check your syntax.", type: 'error'});
    }
  };

  const saveJsonToDatabase = async (data?: any) => {
    const dataToSave = data || jsonData;
    
    if (!document.id || !dataToSave) return;
    
    setSaveStatus({message: "Saving...", type: 'loading'});
    
    try {
      const parsedJson = typeof dataToSave === 'string' ? JSON.parse(dataToSave) : dataToSave;
      
      const response = await fetch(`/documents/${document.id}/update_split_job${splitJob.id ? `?split_job_id=${splitJob.id}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': globalThis.document?.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          split_job: {
            start_pages: parsedJson
          }
        })
      });

      if (response.ok) {
        setSaveStatus({message: "Saved successfully!", type: 'success'});
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        throw new Error('Network response was not ok.');
      }
    } catch (error) {
      console.error('Error saving to split_job:', error);
      setSaveStatus({message: "Error saving to database. Please try again.", type: 'error'});
    }
  };

  const addDivisorSelection = () => {
    setDivisorSelections(prev => [
      ...prev,
      { id: Math.random().toString(), type: '' }
    ]);
  };

  const removeDivisorSelection = (id: string) => {
    setDivisorSelections(prev => prev.filter(selection => selection.id !== id));
  };

  const updateDivisorSelection = (id: string, type: string) => {
    setDivisorSelections(prev => prev.map(selection => 
      selection.id === id ? { ...selection, type } : selection
    ));
  };

  const generatePrompt = () => {
    const selectedDivisors = divisorSelections
      .map(selection => selection.type)
      .filter(type => type !== '')
      .map(type => type.charAt(0).toUpperCase() + type.slice(1));

    if (selectedDivisors.length === 0) {
      setExtractPrompt("Please select at least one divisor type to generate a prompt.");
      return;
    }

    const joinWithConjunction = (arr: string[], conjunction = "and") => {
      if (arr.length === 0) return "";
      if (arr.length === 1) return arr[0];
      if (arr.length === 2) return arr[0] + " " + conjunction + " " + arr[1];
      return arr.slice(0, -1).join(", ") + ", " + conjunction + " " + arr[arr.length - 1];
    };

    const grammaticalDivisors = joinWithConjunction(selectedDivisors, "and");
    
    let promptText = `Extract the ${grammaticalDivisors} metadata for this document following the schema precisely.\n\n`;
    
    selectedDivisors.forEach(divisor => {
      promptText += `There should be [[EDIT THIS TEXT]] ${divisor}s in the ${divisor}s array.\n\n`;
      promptText += `${divisor}s are denoted as [[EDIT THIS TEXT]]\n\n`;
      promptText += `An example of a ${divisor} is: \n[[EDIT THIS TEXT]]\nOR\n[[EDIT THIS TEXT]]\n\n`;
    });

    setExtractPrompt(promptText);
  };

  const generateJsonMap = async () => {
    const selectedDivisors = divisorSelections
      .map(selection => selection.type)
      .filter(type => type !== '');

    if (selectedDivisors.length === 0) {
      setJsonMap("Please select at least one divisor type.");
      return;
    }

    try {
      const response = await fetch(`/documents/${document.id}/extract_jobs/generate_json_map`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': globalThis.document?.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          extract_job: { divisor_types_order: selectedDivisors }
        })
      });

      const data = await response.json();
      
      if (data.json_map) {
        setJsonMap(JSON.stringify(data.json_map, null, 2));
      } else if (data.error) {
        setJsonMap("Error generating JSON map: " + data.error);
      }
    } catch (error) {
      console.error('Error generating JSON map:', error);
      setJsonMap("Error generating JSON map. Check console for details.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = {
      json_data: jsonData,
      overlap_pages: overlapPages ? '1' : '0',
      extract_prompt: extractPrompt,
      json_map: jsonMap,
      chunking_strategy: chunkingStrategy,
      extract_job: {
        divisor_types: divisorSelections.map(s => s.type).filter(t => t !== '')
      }
    };

    router.post(`/documents/${document.id}/create_combined_jobs`, formData);
  };

  return (
    <>
      <Head title={`Edit Split Job - ${document.name}`} />
      
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="space-y-6">
          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, href: `/documents/${document.id}` },
              { label: 'Edit Split Job', current: true }
            ]}
          />

          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Edit Split Job for {document.name}</h1>
            <Button asChild variant="outline">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
          </div>

          {/* Main Form */}
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle className="text-xl text-gray-700">Create Parse and Extract Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Overlap Pages Checkbox */}
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="overlap_pages" 
                    checked={overlapPages}
                    onCheckedChange={(checked) => setOverlapPages(checked as boolean)}
                  />
                  <Label htmlFor="overlap_pages" className="text-gray-700">
                    Allow pages to start and stop on the same page
                  </Label>
                </div>

                {/* Chunking Strategy Selection */}
                <div className="space-y-2">
                  <Label htmlFor="chunking_strategy" className="block text-gray-700 text-sm font-bold">
                    Chunking Strategy for Parse Jobs
                  </Label>
                  <Select 
                    value={chunkingStrategy} 
                    onValueChange={(value) => setChunkingStrategy(value)}
                  >
                    <SelectTrigger id="chunking_strategy">
                      <SelectValue placeholder="Select chunking strategy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="size_aware">Size-Aware Divisor</SelectItem>
                      <SelectItem value="divisor_based">Divisor-Based</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-gray-600 text-xs italic mt-1">
                    {chunkingStrategy === 'size_aware' 
                      ? 'Creates properly-sized chunks (~3000 chars) with divisor metadata and intelligent continuation handling.'
                      : 'Creates chunks based purely on document divisors (sections, articles, etc).'}
                  </p>
                </div>

                {/* JSON Data Section */}
                <div className="pb-6 border-b border-gray-300">
                  <h3 className="text-lg font-semibold mb-4 text-gray-700">JSON Data</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="json_data" className="block text-gray-700 text-sm font-bold mb-2">
                        Enter JSON data for both Parse and Extract Jobs:
                      </Label>
                      <Textarea
                        ref={textareaRef}
                        id="json_data"
                        value={jsonData}
                        onChange={(e) => setJsonData(e.target.value)}
                        placeholder='{ "1": "Chapter One", "5": "Chapter Two" }'
                        rows={6}
                        className="font-mono"
                      />
                      <p className="text-gray-600 text-xs italic mt-1">
                        Format: page number as key, section title as value
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button type="button" onClick={cleanJsonData} className="bg-blue-500 hover:bg-blue-700">
                        Clean JSON
                      </Button>
                      <Button type="button" onClick={() => saveJsonToDatabase()} className="bg-green-500 hover:bg-green-700">
                        Save JSON
                      </Button>
                    </div>

                    {saveStatus && (
                      <div className={`text-sm ${
                        saveStatus.type === 'success' ? 'text-green-500' : 
                        saveStatus.type === 'error' ? 'text-red-500' : 'text-blue-500'
                      }`}>
                        {saveStatus.message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Extract Jobs Configuration */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-700">Extract Jobs Configuration</h3>
                  
                  {/* Divisor Types Selection */}
                  <div className="mb-8">
                    <Label className="block text-gray-700 text-sm font-bold mb-2">
                      Select Nested Divisor Types
                    </Label>
                    
                    <div className="space-y-2">
                      {divisorSelections.map((selection) => (
                        <div key={selection.id} className="flex items-center gap-2">
                          <Select value={selection.type} onValueChange={(value) => updateDivisorSelection(selection.id, value)}>
                            <SelectTrigger className="flex-1">
                              <SelectValue placeholder="Select a divisor type" />
                            </SelectTrigger>
                            <SelectContent>
                              {DIVISOR_TYPES.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {type.charAt(0).toUpperCase() + type.slice(1)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removeDivisorSelection(selection.id)}
                            disabled={divisorSelections.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex flex-col mt-2">
                      <Button 
                        type="button" 
                        onClick={addDivisorSelection}
                        className="bg-blue-500 hover:bg-blue-700 w-fit"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Nested Divisor
                      </Button>
                      <p className="text-gray-600 text-xs italic mt-1">
                        Format: Highest level divisor first, lowest level last
                      </p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-4 mb-6">
                    <Button type="button" onClick={generatePrompt} className="bg-green-500 hover:bg-green-700">
                      Generate Prompt
                    </Button>
                    <Button 
                      type="button" 
                      onClick={() => setShowReadme(!showReadme)}
                      className="bg-yellow-400 hover:bg-yellow-500 text-black"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                    <Button type="button" onClick={generateJsonMap} className="bg-blue-500 hover:bg-blue-700">
                      Generate JSON Map
                    </Button>
                  </div>

                  {/* README Content */}
                  {showReadme && (
                    <Card className="bg-gray-200 mb-6">
                      <CardContent className="p-4">
                        <p>This section helps you configure extract jobs for processing the document sections created by the split job.</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Extract Prompt */}
                  <div className="mb-6">
                    <Label htmlFor="extract_prompt" className="block text-gray-700 text-sm font-bold mb-2">
                      Extract Prompt
                    </Label>
                    <Textarea
                      id="extract_prompt"
                      value={extractPrompt}
                      onChange={(e) => setExtractPrompt(e.target.value)}
                      placeholder="Enter your extract prompt here"
                      rows={5}
                    />
                    <p className="text-gray-600 text-xs italic mt-1">
                      Instructions for the extract prompt
                    </p>
                  </div>

                  {/* JSON Map */}
                  <div className="mb-6">
                    <Label htmlFor="json_map" className="block text-gray-700 text-sm font-bold mb-2">
                      Document JSON Map
                    </Label>
                    <Textarea
                      id="json_map"
                      value={jsonMap}
                      onChange={(e) => setJsonMap(e.target.value)}
                      placeholder="Generate JSON Map from divisor types"
                      rows={5}
                    />
                    <p className="text-gray-600 text-xs italic mt-1">
                      Additional instructions for the extraction process
                    </p>
                  </div>
                </div>

                {/* Submit Section */}
                <div className="flex items-center justify-between pt-8">
                  <Button type="submit" className="bg-green-500 hover:bg-green-700">
                    Create Parse & Extract Jobs
                  </Button>
                  <Button asChild variant="outline">
                    <Link href={`/documents/${document.id}`}>
                      Cancel
                    </Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
